import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Upload,
} from "antd";
import Router from "..";
import { useUserStore } from "../../stores/userStore";
import { UploadOutlined } from "@ant-design/icons";
import { fileToBase64 } from "../utils/fileToBase64";

export const InfluencerForm = ({ form }) => {
  const { userInfo, loadUserInfo } = useUserStore();
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [identificationImg, setIdentificationImg] = useState(null);

  const handleFileSelect = async (file) => {
    const base64 = await fileToBase64(file);
    setIdentificationImg(base64);
    setSelectedFile(file);
    return false; // 자동 업로드 방지
  };

  useEffect(() => {
    if (userInfo?.type_info) {
      form.setFieldsValue({
        nickname: userInfo.type_info.nickname,
        birth: userInfo.type_info.birth
          ? userInfo.type_info.birth.split(" ")[0]
          : undefined,
        gender: userInfo.type_info.gender,
        currency: userInfo.type_info.currency,
        budget: userInfo.type_info.budget,
      });
      if (userInfo?.type_info?.identification_url) {
        setIdentificationImg(userInfo.type_info.identification_url);
      }
    }
  }, [userInfo, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields([
      "nickname",
      "birth",
      "gender",
      "currency",
      "budget",
    ]);

    const updated = await Router.Api.updateAccount({
      ...values,
      user_type: "influencer",
      identification_url: identificationImg,
    });

    if (updated) {
      setIsDisabled(true);
      loadUserInfo();
    }
  };

  const handleCancel = () => {
    setIsDisabled(true);
    setSelectedFile(null);
    setIdentificationImg(null);
    loadUserInfo();
  };

  const renderHeaderButtons = () => {
    if (isDisabled) {
      return (
        <Button
          type="primary"
          htmlType="button"
          size="small"
          className="!bg-[#171717]"
          onClick={() => setIsDisabled(false)}
        >
          수정
        </Button>
      );
    } else {
      return (
        <>
          <Button
            htmlType="button"
            size="small"
            className="!border-[#171717] !text-[#171717]"
            onClick={handleCancel}
          >
            취소
          </Button>
          <Button
            type="primary"
            htmlType="button"
            size="small"
            className="!bg-[#171717]"
            onClick={handleSubmit}
          >
            완료
          </Button>
        </>
      );
    }
  };

  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <div>크리에이터 정보</div>
          <div className="flex gap-2 items-center">{renderHeaderButtons()}</div>
        </div>
      }
      className="w-full max-w-[600px] border border-[#d9d9d9] rounded-[10px] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] mb-[30px]"
    >
      <Form
        form={form}
        layout="vertical"
        disabled={isDisabled}
        requiredMark={false}
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-0">
          <Form.Item
            label="닉네임"
            name="nickname"
            rules={[{ required: true, message: "닉네임을 입력해주세요." }]}
            className="col-span-2 mb-4"
          >
            <Input disabled={isDisabled} />
          </Form.Item>

          <Form.Item
            label="생년월일"
            name="birth"
            rules={[{ required: true, message: "생년월일을 입력해주세요." }]}
            className="mb-4"
          >
            <Input type={isDisabled ? "text" : "date"} disabled={isDisabled} />
          </Form.Item>

          <Form.Item
            label="성별"
            name="gender"
            rules={[{ required: true, message: "성별을 선택해주세요." }]}
            className="mb-4"
          >
            <Select placeholder="선택하세요" disabled={isDisabled}>
              <Select.Option value="male">남성</Select.Option>
              <Select.Option value="female">여성</Select.Option>
              <Select.Option value="unknown">기타</Select.Option>
            </Select>
          </Form.Item>

          {userInfo?.type_info?.tiktok_influencer_id && (
            <>
              <Form.Item
                label="통화"
                name="currency"
                rules={[{ required: true, message: "통화를 선택해주세요." }]}
                className="mb-4"
              >
                <Select placeholder="통화를 선택하세요" disabled={isDisabled}>
                  <Select.Option value="KRW">KRW</Select.Option>
                  <Select.Option value="USD">USD</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="budget"
                label="예산"
                rules={[{ required: true, message: "예산을 입력해주세요." }]}
                className="mb-4"
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  className="w-full"
                  placeholder="예산을 입력해주세요"
                  disabled={isDisabled}
                />
              </Form.Item>
            </>
          )}
        </div>
        <Form.Item
          name="identification_url"
          label="본인 확인 서류"
          rules={[
            {
              required: true,
              message: "본인 확인 서류를 이미지로 등록해주세요.",
            },
          ]}
          className="mb-4"
        >
          <>
            <div className="text-[12px] text-[#666] mb-2">
              사업자일 경우 무조건 사업자 등록증, 사업자가 아닌 경우 신분증이나
              여권을 등록하세요.
            </div>
            <div className="flex gap-4 items-start">
              <Upload
                listType="picture-card"
                beforeUpload={handleFileSelect}
                accept="image/*"
                showUploadList={false}
                disabled={isDisabled}
              >
                <div>
                  <UploadOutlined />
                  <div>업로드</div>
                </div>
              </Upload>

              {(selectedFile || identificationImg) && (
                <div>
                  <Image
                    src={
                      selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : identificationImg
                    }
                    alt="본인 확인 서류"
                    width={102}
                    height={102}
                    className="rounded-lg object-cover border border-[#d9d9d9] cursor-pointer"
                    preview={{
                      mask: false,
                    }}
                  />
                </div>
              )}
            </div>
          </>
        </Form.Item>
      </Form>
    </Card>
  );
};
